import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { TabContent, TabPane, FormGroup, Label } from "reactstrap";
import { BUSINESS_RULES_CATEGORIES, BUSINESS_RULES_DEFINITIONS } from "../../../../models/businessRule";
import { NeoCard, NeoBadge, NeoAlert, NeoButton, NeoInput, NeoTabs } from "../../../../components/Common";

/**
 * BusinessRulesDetail
 * Painel direito do Master-Detail.
 * Exibe as regras de negócio da categoria selecionada com formulário de edição.
 */
const BusinessRulesDetail = ({ categoryId, rules, onSave, isSaving }) => {

    const catInfo = BUSINESS_RULES_CATEGORIES.find(c => c.id === categoryId) || { icon: "mdi-cog", description: "" };
    const categoryRules = BUSINESS_RULES_DEFINITIONS.filter(r => r.category === categoryId);

    const [activeRuleId, setActiveRuleId] = useState("");
    const [formData, setFormData] = useState({});
    const [isDirty, setIsDirty] = useState(false);

    // Selecionar primeira regra ao mudar de categoria
    useEffect(() => {
        if (categoryRules.length > 0) {
            const firstId = categoryRules[0].id;
            setActiveRuleId(firstId);
            setFormData({ ...rules });
            setIsDirty(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    // Sync com rules externas
    useEffect(() => {
        setFormData({ ...rules });
    }, [rules]);

    const handleFieldChange = (ruleId, value) => {
        setFormData(prev => ({ ...prev, [ruleId]: value }));
        setIsDirty(true);
    };

    const handleSubmit = () => {
        onSave(formData);
        setIsDirty(false);
    };

    const activeRule = categoryRules.find(r => r.id === activeRuleId);

    if (!categoryId) {
        return (
            <NeoCard 
                noBorder={true}
                className="h-100 animate__animated animate__fadeIn"
                bodyClassName="d-flex align-items-center justify-content-center p-5"
                bodyStyle={{ minHeight: "400px" }}
            >
                <div className="text-center text-muted">
                    <i className="mdi mdi-arrow-left-bold font-size-48 opacity-25 mb-3 d-block"></i>
                    <p className="font-size-14 mb-0 fw-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Selecione uma categoria para ver as regras.</p>
                </div>
            </NeoCard>
        );
    }

    const tabs = categoryRules.map(rule => ({
        id: rule.id,
        label: rule.label,
        icon: rule.icon
    }));

    return (
        <NeoCard
            title={categoryId}
            subtitle={catInfo.description}
            headerIcon={catInfo.icon}
            thinBorder={true}
            className="h-100 animate__animated animate__fadeIn"
            bodyClassName="p-0 d-flex flex-column h-100"
        >
            {/* Tabs de regras */}
            {categoryRules.length > 1 && (
                <div className="border-bottom flex-shrink-0 pt-2 px-4 bg-light bg-opacity-10">
                    <NeoTabs
                        tabs={tabs}
                        activeTab={activeRuleId}
                        onChangeTab={setActiveRuleId}
                        variant="light"
                    />
                </div>
            )}

            {/* Conteúdo */}
            <TabContent activeTab={activeRuleId} className="p-4 flex-grow-1 overflow-auto custom-scrollbar">
                {activeRule && (
                    <TabPane tabId={activeRuleId} className="animate__animated animate__fadeIn animate__faster">
                        {/* Descrição e título da regra */}
                        <div className="d-flex align-items-start justify-content-between mb-4 pb-3 border-bottom" style={{ borderBottom: "1.5px solid rgba(72,39,18,0.10)" }}>
                            <div className="d-flex align-items-start overflow-hidden me-2">
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                    style={{
                                        width: "44px",
                                        height: "44px",
                                        background: "rgba(226, 102, 28, 0.08)",
                                        border: "1px solid rgba(226, 102, 28, 0.15)",
                                        color: "var(--color-brand-accent)"
                                    }}
                                >
                                    <i className={`mdi ${activeRule.icon} font-size-22`}></i>
                                </div>
                                <div className="ms-3 overflow-hidden">
                                    <h6 className="mb-1 fw-bold text-dark neo-font-outfit font-size-16">{activeRule.label}</h6>
                                    <p className="text-muted small mb-0 font-size-12">{activeRule.description}</p>
                                </div>
                            </div>
                        </div>

                        {/* Campo de configuração */}
                        <div className="mb-4">
                            {activeRule.inputType === 'boolean' ? (
                                <FormGroup className="mb-3">
                                    <Label htmlFor={`input-${activeRule.id}`} className="fw-bold text-muted small text-uppercase mb-2 d-block">
                                        Status da Regra
                                    </Label>
                                    <div className="d-flex align-items-center gap-3">
                                        <NeoBadge color={(formData[activeRule.id] ?? activeRule.defaultValue) ? 'success' : 'muted'}>
                                            {(formData[activeRule.id] ?? activeRule.defaultValue) ? 'Ativado' : 'Desativado'}
                                        </NeoBadge>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                type="checkbox"
                                                id={`input-${activeRule.id}`}
                                                className="form-check-input"
                                                checked={formData[activeRule.id] ?? activeRule.defaultValue}
                                                onChange={e => handleFieldChange(activeRule.id, e.target.checked)}
                                                style={{ 
                                                    cursor: 'pointer', 
                                                    width: '40px', 
                                                    height: '20px',
                                                    marginLeft: 0
                                                }}
                                            />
                                        </div>
                                    </div>
                                </FormGroup>
                            ) : (
                                <NeoInput
                                    label="Valor da Configuração"
                                    type={activeRule.inputType === 'number' ? 'number' : 'text'}
                                    id={`input-${activeRule.id}`}
                                    min={activeRule.inputType === 'number' ? (activeRule.min ?? 0) : undefined}
                                    max={activeRule.inputType === 'number' ? (activeRule.max ?? 999) : undefined}
                                    value={formData[activeRule.id] ?? activeRule.defaultValue}
                                    onChange={e => handleFieldChange(activeRule.id, activeRule.inputType === 'number' ? Number(e.target.value) : e.target.value)}
                                    placeholder={activeRule.helpText || ""}
                                    className="mb-3"
                                    style={{ maxWidth: "320px" }}
                                    flat={true}
                                />
                            )}
                            
                            {activeRule.suffix && activeRule.inputType !== 'boolean' && (
                                <div className="text-muted font-size-12 mt-1.5 fw-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    Unidade: <span className="text-accent">{activeRule.suffix}</span>
                                </div>
                            )}
                        </div>

                        {/* Detalhes de impactos */}
                        {activeRule.impacts && activeRule.impacts.length > 0 && (
                            <div className="mb-4 p-3 bg-light bg-opacity-40 rounded-3" style={{ border: '1px solid rgba(72,39,18,0.06)' }}>
                                <h6 className="font-size-12 fw-bold text-muted text-uppercase mb-3" style={{ letterSpacing: '0.5px' }}>
                                    Impactos e Ações Automáticas
                                </h6>
                                <div className="d-flex flex-column gap-2">
                                    {activeRule.impacts.map((imp, idx) => (
                                        <div key={idx} className="d-flex align-items-center gap-2">
                                            <i className={`mdi ${imp.icon} text-accent font-size-16`}></i>
                                            <span className="font-size-12 text-muted fw-semibold">{imp.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Alerta de valor 0 - APENAS para regras numéricas onde 0 tem impacto imediato */}
                        {activeRule.inputType !== 'boolean' && (formData[activeRule.id] ?? activeRule.defaultValue) === 0 && activeRule.id === 'membershipGraceDays' && (
                            <NeoAlert 
                                color="warning"
                                title="Aviso Importante"
                            >
                                Com <strong>0 dias</strong>, o aluno será inativado imediatamente na data de vencimento do contrato.
                            </NeoAlert>
                        )}

                        {/* Botão salvar */}
                        <div className="d-flex justify-content-end pt-3 border-top" style={{ borderTop: "1.5px solid rgba(72,39,18,0.10)" }}>
                            <NeoButton
                                variant="primary"
                                className="px-5 fw-bold text-white" 
                                loading={isSaving}
                                onClick={handleSubmit}
                                disabled={!isDirty}
                            >
                                Salvar Alterações
                            </NeoButton>
                        </div>
                    </TabPane>
                )}
            </TabContent>
        </NeoCard>
    );
};

BusinessRulesDetail.propTypes = {
    categoryId: PropTypes.string,
    rules: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool
};

export default BusinessRulesDetail;
